import { app } from "@microsoft/teams-js";
import { SurveyDTO } from "@teams/types/api";
import { useCallback, useState } from "react";

import { isSuccess } from "../../_shared/services/ChannelApi";
import { scheduleApi } from "../../_shared/services/ScheduleApi";

type LoadHistoricalSurveysOptions = {
  includeResponses?: boolean;
  pageIndex?: number;
};
export const useHistoricalSurveys = () => {
  const [isHistoricalSurveysLoading, setIsHistoricalSurveysLoading] = useState<boolean>(true);
  const [historicalSurveys, setHistoricalSurveys] = useState<SurveyDTO[]>([]);
  const [historicalSurveyTotal, setHistoricalSurveyTotal] = useState<number>(0);
  const [error, setError] = useState<Error>();

  const basePageSize = 10;
  const pageIncrementBase = 25;

  const getHistoricalSurveyLoadCount = useCallback(
    (pageIndex: number) => {
      // -1 -> 10
      // 0 -> 25 = 25 * 2^0
      // 1 -> 50 = 25 * 2^1
      // 2 -> 100 = 25 * 2^2
      // 3 -> 200 = 25 * 2^3
      if (pageIndex < 0) {
        return basePageSize;
      }
      const nextLoadCount = pageIncrementBase * Math.pow(2, pageIndex);
      const remainingCount = historicalSurveyTotal - historicalSurveys.length;
      if (remainingCount < nextLoadCount) {
        return remainingCount;
      }
      return nextLoadCount;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const loadHistoricalSurveys = useCallback(
    async (
      context: app.Context,
      seriesId: string,
      options: LoadHistoricalSurveysOptions,
      groupId?: string
    ) => {
      setIsHistoricalSurveysLoading(true);
      setError(undefined);
      options.pageIndex = options.pageIndex || 0;
      const surveysResponse = await scheduleApi.getHistoricalSurveys(
        context,
        seriesId,
        {
          ...options,
          skip: historicalSurveys.length,
          limit: getHistoricalSurveyLoadCount(options.pageIndex - 1),
        },
        groupId
      );
      if (isSuccess(surveysResponse)) {
        setHistoricalSurveys([...historicalSurveys, ...surveysResponse.surveys]);
        setHistoricalSurveyTotal(surveysResponse.total);
      } else {
        setError(surveysResponse);
      }

      setIsHistoricalSurveysLoading(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return {
    error,
    loadHistoricalSurveys,
    isHistoricalSurveysLoading,
    historicalSurveys,
    historicalSurveyTotal,
    getHistoricalSurveyLoadCount,
  };
};
